package com.mgacreative.hotel.feature.room.ui

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.mgacreative.hotel.domain.repository.ReservationRepository
import com.mgacreative.hotel.domain.repository.RoomRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

data class RoomTypeSummary(
    val roomType: String,
    val totalRooms: Int,
    val occupiedCount: Int,
    val reservedCount: Int,
    val emptyCount: Int
)

data class GlobalRoomSummary(
    val totalRooms: Int = 0,
    val totalOccupied: Int = 0,
    val totalReserved: Int = 0,
    val totalEmpty: Int = 0,
    val occupancyRatePercentage: Double = 0.0
)

class RoomSummaryViewModel(
    private val roomRepository: RoomRepository,
    private val reservationRepository: ReservationRepository
) : ViewModel() {

    private val _globalSummary = MutableStateFlow(GlobalRoomSummary())
    val globalSummary: StateFlow<GlobalRoomSummary> = _globalSummary.asStateFlow()

    private val _roomTypeSummaries = MutableStateFlow<List<RoomTypeSummary>>(emptyList())
    val roomTypeSummaries: StateFlow<List<RoomTypeSummary>> = _roomTypeSummaries.asStateFlow()

    private val _isLoading = MutableStateFlow(false)
    val isLoading: StateFlow<Boolean> = _isLoading.asStateFlow()

    init {
        loadSummaryData()
    }

    fun loadSummaryData() {
        viewModelScope.launch {
            _isLoading.value = true
            try {
                val rooms = roomRepository.getRooms()
                
                var globalOccupied = 0
                var globalReserved = 0
                var globalEmpty = 0

                val groupedByType = rooms.groupBy { it.roomType.ifBlank { "Diğer" } }

                val typeSummaries = groupedByType.map { (type, typeRooms) ->
                    var occupied = 0
                    var reserved = 0
                    var empty = 0

                    typeRooms.forEach { room ->
                        val status = room.status.uppercase()
                        when {
                            status.contains("OCCUPIED") || status.contains("FULL") || status.contains("DOLU") -> {
                                occupied++
                                globalOccupied++
                            }
                            status.contains("RESERV") || status.contains("REZERVE") -> {
                                reserved++
                                globalReserved++
                            }
                            else -> {
                                empty++
                                globalEmpty++
                            }
                        }
                    }

                    RoomTypeSummary(
                        roomType = type,
                        totalRooms = typeRooms.size,
                        occupiedCount = occupied,
                        reservedCount = reserved,
                        emptyCount = empty
                    )
                }.sortedBy { it.roomType }

                val total = rooms.size
                val rate = if (total > 0) (globalOccupied.toDouble() / total * 100) else 0.0

                _globalSummary.value = GlobalRoomSummary(
                    totalRooms = total,
                    totalOccupied = globalOccupied,
                    totalReserved = globalReserved,
                    totalEmpty = globalEmpty,
                    occupancyRatePercentage = rate
                )

                _roomTypeSummaries.value = typeSummaries
            } catch (e: Exception) {
                e.printStackTrace()
            } finally {
                _isLoading.value = false
            }
        }
    }
}
